/**
 * LearnCourses.jsx — SmarterCourses catalog hub.
 *
 * The catalog is authenticated but remains browseable; opening a course is
 * governed by the existing modules trial gate.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Award, BookOpen, Briefcase, ChevronRight, GraduationCap, Headphones,
  LayoutGrid, Loader2, PenSquare, RefreshCw, Sparkles, Users,
} from 'lucide-react';
import { authFetch } from '../utils/authUtils';
import TrialGateBanner from '../components/TrialGateBanner';
import useTrialGate from '../hooks/useTrialGate';
import CourseCertificateModal from '../components/CourseCertificateModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PATH_ICONS = {
  writer: { Icon: PenSquare, color: 'text-purple-200', bg: 'bg-purple-400/10', border: 'border-purple-300/30' },
  reader: { Icon: BookOpen, color: 'text-cyan-200', bg: 'bg-cyan-400/10', border: 'border-cyan-300/30' },
  listener: { Icon: Headphones, color: 'text-emerald-200', bg: 'bg-emerald-400/10', border: 'border-emerald-300/30' },
  ambassador: { Icon: Users, color: 'text-rose-200', bg: 'bg-rose-400/10', border: 'border-rose-300/30' },
  platform: { Icon: LayoutGrid, color: 'text-amber-200', bg: 'bg-amber-400/10', border: 'border-amber-300/30' },
  business: { Icon: Briefcase, color: 'text-indigo-200', bg: 'bg-indigo-400/10', border: 'border-indigo-300/30' },
};

const LearnCourses = () => {
  const navigate = useNavigate();
  const [paths, setPaths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [certModal, setCertModal] = useState({ open: false, courseId: null });
  const { status: trialStatus, blocked: trialBlocked } = useTrialGate('modules', { peek: true });

  const loadCatalog = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await authFetch(`${BACKEND_URL}/api/learn/courses/catalog`);
      if (!response.ok) throw new Error('Catalog request failed');
      const data = await response.json();
      setPaths(Array.isArray(data.paths) ? data.paths : []);
    } catch (loadError) {
      setPaths([]);
      setError('We could not load learning paths right now.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadCatalog(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const catalogTotals = useMemo(() => paths.reduce((totals, path) => ({
    courses: totals.courses + (path.course_count ?? 0),
    authored: totals.authored + (path.flagship_count ?? 0),
    passed: totals.passed + (path.passed_count ?? 0),
  }), { courses: 0, authored: 0, passed: 0 }), [paths]);

  const openCourse = (courseId) => {
    if (!trialBlocked) navigate(`/learn/courses/${courseId}`);
  };

  return (
    <main className="max-w-6xl mx-auto px-3 sm:px-5 py-5 sm:py-8" data-testid="learn-courses-page">
      <section className="relative overflow-hidden rounded-3xl border border-purple-300/20 bg-[#0b1024] px-5 py-6 sm:px-8 sm:py-8 shadow-[0_20px_70px_rgba(5,8,30,0.35)]">
        <div aria-hidden="true" className="absolute -right-20 -top-24 h-64 w-64 rounded-full bg-purple-500/20 blur-3xl" />
        <div aria-hidden="true" className="absolute -left-16 bottom-0 h-40 w-40 rounded-full bg-cyan-400/10 blur-3xl" />
        <div className="relative flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
          <div className="max-w-2xl">
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-amber-300/25 bg-amber-300/10 px-3 py-1 text-[10px] font-black uppercase tracking-[0.18em] text-amber-200">
              <GraduationCap className="h-3.5 w-3.5" aria-hidden="true" /> SmarterCourses
            </div>
            <h1 className="font-display text-3xl font-black tracking-tight text-white sm:text-5xl" data-testid="learn-courses-title">
              Choose your next learning path.
            </h1>
            <p className="mt-3 max-w-xl text-sm leading-6 text-slate-300 sm:text-base">
              Browse the courses available in every path, then continue at your own pace.
            </p>
          </div>
          {!loading && !error && (
            <div className="grid grid-cols-3 divide-x divide-white/10 self-stretch rounded-2xl border border-white/10 bg-[#111936]/80 text-center sm:min-w-[290px]">
              <div className="px-3 py-3"><p className="text-lg font-black tabular-nums text-white">{catalogTotals.courses}</p><p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Courses</p></div>
              <div className="px-3 py-3"><p className="text-lg font-black tabular-nums text-white">{catalogTotals.authored}</p><p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Authored</p></div>
              <div className="px-3 py-3"><p className="text-lg font-black tabular-nums text-white">{catalogTotals.passed}</p><p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Completed</p></div>
            </div>
          )}
        </div>
      </section>

      <div className="mt-5"><TrialGateBanner status={trialStatus} feature="modules" /></div>

      {loading && (
        <div className="flex min-h-[280px] flex-col items-center justify-center gap-3" role="status" aria-live="polite">
          <Loader2 className="h-8 w-8 animate-spin text-cyan-200 motion-reduce:animate-none" aria-hidden="true" />
          <p className="text-sm text-slate-300">Loading learning paths…</p>
        </div>
      )}

      {!loading && error && (
        <section className="mt-5 rounded-3xl border border-rose-300/25 bg-rose-400/5 px-5 py-8 text-center" role="alert">
          <h2 className="font-display text-xl font-black text-white">Learning paths are unavailable</h2>
          <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-slate-300">{error}</p>
          <button type="button" onClick={loadCatalog} className="mt-5 inline-flex min-h-11 items-center gap-2 rounded-xl border border-white/15 bg-white/10 px-4 text-sm font-bold text-white transition-colors hover:bg-white/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300 focus-visible:ring-offset-2 focus-visible:ring-offset-[#090d20] motion-reduce:transition-none">
            <RefreshCw className="h-4 w-4" aria-hidden="true" /> Try again
          </button>
        </section>
      )}

      {!loading && !error && paths.length === 0 && (
        <section className="mt-5 rounded-3xl border border-white/10 bg-[#10162d] px-5 py-10 text-center">
          <GraduationCap className="mx-auto h-8 w-8 text-slate-400" aria-hidden="true" />
          <h2 className="mt-3 font-display text-xl font-black text-white">No learning paths are available yet</h2>
        </section>
      )}

      {!loading && !error && paths.length > 0 && (
        <div className="mt-5 space-y-5" aria-label="Learning paths">
          {paths.map((path) => {
            const style = PATH_ICONS[path.id] || PATH_ICONS.reader;
            const PathIcon = style.Icon;
            return (
              <section key={path.id} className="overflow-hidden rounded-3xl border border-white/10 bg-[#10162d] shadow-[0_12px_32px_rgba(0,0,0,0.16)]" data-testid={`learn-courses-path-${path.id}`}>
                <header className="flex items-center gap-3 border-b border-white/10 bg-white/[0.025] px-4 py-4 sm:px-5">
                  <div className={`grid h-11 w-11 shrink-0 place-items-center rounded-2xl border ${style.border} ${style.bg}`}><PathIcon className={`h-5 w-5 ${style.color}`} strokeWidth={1.7} aria-hidden="true" /></div>
                  <div className="min-w-0 flex-1">
                    <p className={`text-[10px] font-black uppercase tracking-[0.18em] ${style.color}`}>Learning path</p>
                    <h2 className="truncate font-display text-xl font-black tracking-tight text-white">{path.title}</h2>
                  </div>
                  <span className="hidden shrink-0 rounded-full border border-white/10 bg-black/20 px-3 py-1.5 text-[10px] font-black tabular-nums uppercase tracking-wider text-slate-300 sm:inline-block">
                    {path.passed_count}/{path.course_count} completed
                  </span>
                </header>
                <ul className="divide-y divide-white/5">
                  {path.courses.map((course) => (
                    <li key={course.id} className="flex items-center gap-2 px-2 py-1.5 sm:px-3">
                      <button type="button" disabled={trialBlocked} onClick={() => openCourse(course.id)} className="group flex min-h-11 min-w-0 flex-1 items-center gap-3 rounded-2xl px-3 text-left transition-colors hover:bg-white/[0.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300 disabled:cursor-not-allowed disabled:opacity-50 motion-reduce:transition-none" data-testid={`learn-course-row-${course.id}`} aria-label={`${course.title}${course.passed ? ', completed' : ''}${trialBlocked ? ', unavailable while modules are locked' : ''}`}>
                        <span className="w-7 shrink-0 text-center text-[10px] font-black tabular-nums text-slate-500">{course.id}</span>
                        <span className="min-w-0 flex-1 text-sm font-semibold text-slate-100 group-hover:text-white">{course.title}</span>
                        {course.authored && <span className="hidden items-center gap-1 text-[10px] font-black uppercase tracking-wider text-amber-200 sm:inline-flex"><Sparkles className="h-3 w-3" aria-hidden="true" /> Authored</span>}
                        {course.passed && <span className="rounded-full border border-emerald-300/25 bg-emerald-300/10 px-2 py-1 text-[9px] font-black uppercase tracking-wider text-emerald-200">Completed</span>}
                        <ChevronRight className="h-4 w-4 shrink-0 text-slate-400 group-hover:text-cyan-200" aria-hidden="true" />
                      </button>
                      {course.passed && <button type="button" onClick={() => setCertModal({ open: true, courseId: course.id })} className="inline-flex min-h-11 shrink-0 items-center gap-1 rounded-xl px-3 text-[10px] font-black uppercase tracking-wider text-amber-200 transition-colors hover:bg-amber-300/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300" data-testid={`learn-cert-view-${course.id}`} aria-label={`View certificate for ${course.title}`}><Award className="h-3.5 w-3.5" aria-hidden="true" /> <span className="hidden sm:inline">Certificate</span></button>}
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      )}
      <CourseCertificateModal courseId={certModal.courseId} open={certModal.open} onOpenChange={(open) => setCertModal((current) => ({ ...current, open }))} variant="view" />
    </main>
  );
};

export default LearnCourses;
